import React from "react";
import { Control, actions } from "react-redux-form";
import { Row, Col } from "reactstrap";
import { compose, withHandlers, withPropsOnChange } from "recompose";
import shortid from "shortid";
import { get, pick } from "lodash";
import { Trans } from "react-i18next";
import { NotificationManager } from "react-notifications";
import {
  FormGroup,
  DatePicker,
  required,
  Page,
  onMount,
  withReload,
  withLocalForm,
} from "components/common";
import {
  withXeroErrors,
  LineItems,
  SalesEditActions,
  withContactSearch,
  Attachments,
  withOptions,
  withDocumentRenderer,
} from "components";
import {
  BillCreditNoteDocument,
  InvoiceCreditNoteDocument,
} from "components/documents";
import { format_reference, formatDate } from "../../../services/util";
import moment from "moment";
import Payment from "../../../components/Payment";
import { withOrganisationContextConsumer } from "../../../contexts/OrganisationContext";
import BackButton from "components/refactoring/BackButton";
import {filterAndValidateLineItems, getBlankLineItems} from "../../../components/LineItems";

const accPayCheck = /purchases|payable|accounts_payable/

const isPayable = accPayCheck.test(window.location.href);

const view_link = isPayable ? "purchases" : "accounts_receivable"

export const EditCreditNote = ({
  item,
  LocalForm,
  onSubmit,
  xero,
  handleSave,
  dispatch,
  handlePaymentAdded,
  handleAttachmentsChanged,
  ContactSearch,
  CreditNoteOptions,
  XeroErrors,
  t,
  payable,
  DocumentRenderer,
  EmailDocument,
  DownloadDocument,
  i18n,
  history,
  organisation,
}) => {

  const viewLink = () => {
    const { CreditNoteID } = item;

    history.push(`/${view_link}/credit_notes/view/${CreditNoteID}`);
  };

  return (

  <div>
    <Page
      title={
        item.CreditNoteID
          ? t(`Edit Credit Note {{ CreditNoteNumber }}`, {
              CreditNoteNumber: item.CreditNoteNumber,
            })
          : t("Create New Credit Note")
      }
      translate={false}
    >
      <BackButton/>
      <DocumentRenderer
        component={payable ? BillCreditNoteDocument : InvoiceCreditNoteDocument}
      />
      <XeroErrors />
      <div className="d-flex align-items-center" style={{paddingBottom:20}}>
        {item.SentToContact && (
          <div className="text-success small">
            <span className="fa fa-check mr-1" /> <Trans>Sent to contact</Trans>
          </div>
        )}
        <div className="mx-auto" />
        {!!item.CreditNoteID && <DownloadDocument className="mr-2" />}
        {!!(item.CreditNoteID && !payable) && (
          <EmailDocument type="CREDIT_NOTE" item={item} />
        )}
        {!!(item.CreditNoteID && item.Attachments) && (
          <Attachments
            xero={xero}
            hideIncludeOnline
            onAttachmentsChanged={handleAttachmentsChanged}
            baseUrl={`creditnotes/${item.CreditNoteID}`}
            attachments={item.Attachments}
            className="mr-0"
          />
        )}
        {item.CreditNoteID && <CreditNoteOptions className="m-0"/>}
        {!!item.CreditNoteID && 
          <div>
            <button
              type="button"
              className="btn-sm btn btn-primary"
              onClick={viewLink}>
              <Trans>View Credit Note</Trans>
            </button>
          </div>
        }
      </div>
      <LocalForm
        onSubmit={onSubmit}
        validators={{
          Contact: { required },
          Date: { required },
        }}
      >
        <Row>
          <Col xs={false} md={6}>
            <FormGroup
              label={payable ? "From" : "To"}
              model=".Contact"
              addControlClass={false}  
            >
              <ContactSearch model=".Contact" style={{backgroundColor:'#F4F7FE'}}/>
            </FormGroup>
          </Col>
          <Col xs={false} md={6}>
            <FormGroup label="Status">
              <Control.text model=".Status" readOnly />
            </FormGroup>
          </Col>
          <Col xs={false} md={6}>
            <FormGroup label="Date" model=".Date" className="pt-2 pb-2">
              <DatePicker model=".Date" className="form-control" />
            </FormGroup>
          </Col>
          {!payable && (
            <Col xs={false} md={6}>
              <FormGroup label="Credit Note #" model=".CreditNoteNumber" className="pt-2 pb-2">
                <Control.text
                  readOnly={!item.CreditNoteID}
                  updateOn="blur"
                  model=".CreditNoteNumber"
                />
              </FormGroup>
            </Col>
          )}
          <Col xs={false} md={6}>
            <FormGroup label="Reference" model=".Reference">
              <Control.text updateOn="blur" model=".Reference" />
            </FormGroup>
          </Col>
          {!isPayable &&<Col xs={false} md={6}>
            <FormGroup label="Reason (required for KSA)" model=".Reason">
              <Control.text updateOn="blur" model=".Reason" />
            </FormGroup>
          </Col>}
          {!isPayable && <Col xs={false} md={6} className="pt-2 pb-2">
              <FormGroup label="Document Type" model=".DocumentType">
                <Control.select model=".DocumentType" defaultValue={item.DocumentType}>
                  <option disabled selected value> ------- </option>
                  {[{'label': 'Tax Invoice', 'value': 'standard'}, {'label': 'Simplified Invoice', 'value': 'simplified'}].map((i) => (
                        <option key={i.value} value={i.value}>
                          {i.label}
                        </option>
                    ))}
                </Control.select>
              </FormGroup>
            </Col>}
        </Row>
        <hr />
        <LineItems
          paymentText="Less Cash Refund"
          item={item}
          dispatch={dispatch}
          xero={xero}
          isNewInvoice={item.CreditNoteID ? false : true}
        />
        <SalesEditActions onSave={handleSave} item={item} />
      </LocalForm>
    </Page>
    {item.Status === "AUTHORISED" && (
      <>
        <hr />
        <Payment
          creditNote={item}
          organisation={organisation}
          onPaymentAdded={handlePaymentAdded}
          xero={xero}
          paymentType="Refund"
        />
      </>
    )}
  </div>
)};

const enhance = compose(
  withOrganisationContextConsumer,
  withPropsOnChange(["payable"], ({ payable }) => {
    const type = payable ? "ACCPAYCREDIT" : "ACCRECCREDIT";
    return {
      type,
      payable: !!payable,
    };
  }),
  withReload(),
  onMount(({ match, xero, location, type, fetchOrganisation }) => () => {
    const { id } = match.params;

    let item = null;

    if (id === "new") {
      item = get(location, "state.copy") || {
        LineItems: getBlankLineItems(),
        Status: "DRAFT",
        Date: formatDate(moment()),
        Type: type,
      };
      item = {...item, Reference: format_reference(item.Reference)}
    } else {
      item = xero
        .get(`creditnotes/${match.params.id}`)
        .get("data")
        .then((x) => ({
          ...x,
          LineItems: x.LineItems.map((i) => ({
            ...i,
            _id: shortid(),
          })),
        }));
    }

    return Promise.props({
      item,
      organisation: fetchOrganisation(),
    });
  }),
  withLocalForm({
    initialState: ({ item }) => item,
    stateKey: "item",
    formKey: "form",
  }),
  withContactSearch(),
  withXeroErrors(),
  withHandlers({
    handleAttachmentsChanged:
      ({ xero, match, dispatch }) =>
      () => {
        xero
          .get(`creditnotes/${match.params.id}/attachments`)
          .get("data")
          .tap((d) => dispatch(actions.change("local.Attachments", d)));
      },
    handleSave:
      ({ item, xero, reload, history, match, setXeroErrors, t, form, dispatch }) =>
      ({ draft, approve, submit, addAnother } = {}) => {

        const incompleteForm = "Incomplete Form"
        const contactValid = form.Contact?.$form?.valid !== undefined
          ? form?.Contact?.$form?.valid
          : form?.Contact?.valid;
        const dateValid = form?.Date?.valid || false
        const dueDateValid = item?.DueDate
        const DocumentType = item?.DocumentType

        if (!contactValid) {
          NotificationManager.error(t("Please Enter A Contact Name"), incompleteForm);
        } else if (!dateValid) {
          NotificationManager.error(t("The Date is empty, please add a date."), incompleteForm);
        } else if (dueDateValid === null) {
          NotificationManager.error(t("Due Date is empty, please add a duedate."), incompleteForm);
        } else if (!DocumentType && !isPayable) {
          NotificationManager.error(t("Please Enter A Document Type"), incompleteForm);
          return
          }

        const { result: LineItems, error } = filterAndValidateLineItems(
          dispatch
        )(item.LineItems);

        if (error) {
          return;
        }

        let Status = item.Status;
        if (draft) Status = "DRAFT";
        if (approve) Status = "AUTHORISED";
        if (submit) Status = "SUBMITTED";
        setXeroErrors(null);
        const { CreditNoteID, Allocations } = item;
        const isNew = !CreditNoteID;

        const allowedItem = pick(
          item,
          "Type,Contact.ContactID,Date,Status,LineAmountTypes,LineItems,CurrencyCode,CreditNoteNumber,Reference,Reason,AllocationInvoice,DocumentType".split(
            ","
          )
        );

        const p = CreditNoteID
          ? xero.put(`creditnotes/${CreditNoteID}`, {
              ...allowedItem,
              Status,
              LineItems,
            })
          : xero.post("creditnotes", {
              ...allowedItem,
              CreditNoteNumber: undefined,
              Status,
              LineItems,
            });

        p.then(({ data: { CreditNoteID } }) => {
          NotificationManager.success(t("Credit Note saved successfully"));
          let allocationsPromise = null;

          if (isNew && Status === "AUTHORISED" && Allocations) {
            allocationsPromise = Promise.mapSeries(Allocations, (v, idx) =>
              xero.post(`creditnotes/${CreditNoteID}/allocations`, [
                {
                  AppliedAmount: v.Amount,
                  Invoice: v.Invoice,
                },
              ])
            ).catch((err) => {
              NotificationManager.error(t("Unable to apply credit to invoice"));
            });
          } else {
            allocationsPromise = Promise.resolve();
          }

          return allocationsPromise.finally(() => {
            if (addAnother) {
              history.push(match.path.replace(":id", "new"));
            } else {
              history.replace(`/${view_link}/credit_notes/view/${CreditNoteID}?auto_report=true`);
            }
            reload();
          });
        }, setXeroErrors);
      },
    handlePaymentAdded:
      ({ reload }) =>
      () => {
        reload();
      },
  }),
  withOptions(),
  withDocumentRenderer({
    onDownload:
      ({ item }) =>
      (getDocument) =>
        getDocument(
          {
            item,
          },
          `creditnote_${item.CreditNoteNumber}.pdf`
        ),
  })
);

export default enhance(EditCreditNote);
